import type { ApiClient } from './client';

export interface PublishedSource {
  source_state?: string;
  capabilities?: {
    published_help?: boolean;
    live_status?: boolean;
    automatic_monitoring?: boolean;
    support_agent?: boolean;
    support_dictation?: boolean;
    dictation?: boolean;
  };
}
export interface SupportAction {
  kind: 'tool' | 'collection' | 'screen' | 'sheet';
  id: string;
  title?: string | null;
}
export type SupportBlock =
  | { type: 'p' | 'price'; text: string }
  | { type: 'steps'; items: string[]; open?: boolean }
  | { type: 'sections'; items: Array<{ icon?: string; title: string; numbered?: boolean; open?: boolean; rows: string[] }> }
  | { type: 'table'; title: string; icon?: string; cols: string[]; rows: string[][] }
  | { type: 'refs'; items: Array<{ kind: 'faq' | 'guide' | 'telegram'; id: string }> };
export interface SupportReply {
  blocks: SupportBlock[];
  actions: SupportAction[];
  related: string[];
}
export interface SupportAnswer {
  conversation_id: string;
  message_id: string;
  reply: SupportReply;
  no_answer: boolean;
  status?: never;
}
export interface SupportPending {
  conversation_id: string;
  message_id: string;
  status: 'processing';
  poll_url?: string;
}
export type SupportResult = SupportAnswer | SupportPending;
export interface SupportQuestion {
  conversation_id?: string;
  message: string;
}
export interface SupportRating {
  conversation_id: string;
  message_id: string;
  value: -1 | 0 | 1;
}
export interface HelpTopic {
  id: string;
  title: string;
  icon?: string;
}
export interface HelpFaq {
  id: string;
  topic: string;
  question: string;
  answer: string;
  popular?: boolean;
}
export interface HelpGuide {
  id: string;
  group: string;
  title: string;
  subtitle?: string;
  price?: string;
  steps: Array<string | { title: string; text?: string }>;
  tips?: string[];
  open?: string | SupportAction;
}
export interface HelpSource extends PublishedSource {
  release_version?: string;
  locale?: string;
  topics?: HelpTopic[];
  faq?: HelpFaq[];
  guides?: HelpGuide[];
  guide_groups?: Array<{ id: string; title: string }>;
  quick_questions?: string[];
  support_contact?: { telegram: string } | null;
  onboarding?: { available: boolean; guides?: string[] };
}
export interface ServiceStatus extends PublishedSource {
  checked_at?: string | null;
  as_of?: string;
  source_kind?: string;
  services?: Array<{ id: string; title?: string; state: string; reason?: string }>;
}

export const helpContent = (client: ApiClient) => ({
  help: (signal?: AbortSignal) => client.request<HelpSource>('/app/help', { signal }),
  status: (signal?: AbortSignal) => client.request<ServiceStatus>('/app/status', { signal }),
  ask: (body: SupportQuestion, key: string) =>
    client.request<SupportResult>('/app/support/agent', { method: 'POST', body, idempotency: key }),
  agentMessage: (id: string, signal?: AbortSignal) =>
    client.request<SupportResult>(`/app/support/agent/messages/${encodeURIComponent(id)}`, { signal }),
  rate: (body: SupportRating) => client.request<void>('/app/support/agent/rating', { method: 'POST', body }),
  dictation: (uploadId: string, key: string, signal?: AbortSignal) =>
    client.request<{ text: string; dev_only: boolean }>('/app/support/agent/dictation', {
      method: 'POST',
      body: { upload_id: uploadId },
      idempotency: key,
      signal,
    }),
});
